"use client";

import { XMarkIcon } from "@heroicons/react/24/solid";
import Image from "next/image";
import posthog from "posthog-js";
import { useEffect } from "react";

interface TeamModalProps {
    member: TeamMember;
    onClose: () => void;
}

export default function TeamModal({ member, onClose }: TeamModalProps) {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        posthog?.capture("team_member_clicked", {
            memberName: member.name,
            sectionOpen: true,
        });

        return () => {
            document.body.style.overflow = "unset";
            posthog?.capture("team_member_clicked", {
                memberName: member.name,
                sectionOpen: false,
            });
        };
    }, [member, posthog]);

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[904px] relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute right-6 top-6 p-2"
                    aria-label="Close modal"
                >
                    <XMarkIcon className="w-10 h-10 text-black dark:text-white opacity-30" />
                </button>

                <div className="p-8 flex gap-5">
                    <div className="relative hidden md:block">
                        <Image
                            src={member.image}
                            alt={member.name}
                            width={0}
                            height={0}
                            sizes="100vw"
                            style={{
                                width: "100%",
                                height: "auto",
                            }}
                        />
                    </div>

                    <div className="flex flex-col gap-8 max-w-[475px]">
                        <div className="flex flex-col gap-2">
                            <h2 className="text-[#333333] dark:text-white text-[38px] font-medium font-satoshi leading-[42px]">
                                {member.name}
                            </h2>
                            <p className="text-[#1a5b77] dark:text-blue-400 opacity-50 text-lg font-normal font-space leading-[25px]">
                                {member.title}
                            </p>
                        </div>

                        <div className="flex flex-col gap-4">
                            {member.description
                                .split("\n\n")
                                .map((paragraph, index) => (
                                    <p
                                        key={index}
                                        className="text-[#333333] dark:text-gray-200 text-base font-light font-space leading-snug"
                                    >
                                        {paragraph}
                                    </p>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
